<template>
  <div class="select-item-box" >
    <div class="relative">
      <h5>{{ title }}</h5>
      <b-button
        v-if="selectedTags.length"
        class="clear-selected flex center-all"
        @click.stop="clearSelected" >
        <span>Clear</span>
      </b-button>
    </div>
    <div
      v-if="showSearch"
      class="search-input-wrapper">
      <b-form-input
        ref="searchTermInputRef"
        v-model="searchTerm"
        type="text"
        :placeholder="searchTermPlaceholder"
        @keydown.native.stop="handleKeyDown" />
      <!-- v-show because the icon removed on its own click would be seen as a click outside of the filter popover which would close it -->
      <SBIcon
        v-show="searchTerm"
        :icon="SBIconList.CLOSE_THIN"
        :color="SBColors.blackUltralight"
        :hover-icon-color="SBColors.blackPrimary"
        :size="8"
        :hover-size="16"
        :pointer="true"
        @click="clearSearchTerm"
        @click.native.stop />
    </div>
    <ul
      ref="itemListWrapperRef"
      class="tag-list">
      <!-- Click is not propagated because the tick icon is removed on click and the click outside directive of the filter popover would close it. -->
      <li
        v-for="(tag, index) in availableTags"
        :key="tag.id"
        class="flex flex-align-center"
        :class="{ selected: selectedId === index }"
        @click.stop="toggleTag(tag)"
        @mouseover="mouseSelect(index)"
        @mouseout="deselect()" >
        <img
          v-if="tag.icons"
          class="item-icon tag-icon no-shrink"
          :src="tag.icons.dark" >
        <SBIcon
          v-else
          class="item-icon"
          :icon="SBIconList.TAG"
          :color="SBColors.blackPrimary"
          :size="12"
          :hover-size="20" />
        <span class="greedy ellipsis">{{ tag.name }}</span>
        <span
          v-if="isSelected(tag)"
          class="sb2-icon done-tick-blue size-xxs no-shrink"/>
      </li>
      <li
        v-if="hasNextPage"
        class="list-info flex center-all">
        <div v-observe-visibility="endOfTagsList">
          <Spinner
            :size="17"
            :thickness="2" />
        </div>
      </li>
      <li
        v-else-if="!availableTags.length"
        class="list-info no-match" >
        <template v-if="searchTerm"><span class="emoji-fix">😐</span> We couldn't find anything for <b>{{ searchTerm }}</b>.</template>
        <template v-else>There are no contact tags.</template>
      </li>
    </ul>
  </div>
</template>

<script>
import { debounce } from 'debounce';
import { mapGetters } from 'vuex';
import api from '@/helpers/api';
import Spinner from '@/components/common/Spinner';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';

/**
 * Multiselect list of the organisation contact tags with search and paging.
 * It looks the same as AssignConversationAssignMenu so both can be used in the conversations filter.
 */
export default {
  name: 'ContactTagSelectMenu',
  components: {
    SBIcon,
    Spinner,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Contact tags',
    },
    searchTermPlaceholder: {
      type: String,
      required: false,
      default: 'Search',
    },
    selectedTags: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      searchTerm: '',
      availableTags: [],
      tagPage: 0,
      maxTagPages: null,
      tagsLoading: false,
      lastTagsRequest: 0,
      // Search stays visible once shown so it doesn't disappear while the search results are loading.
      showSearch: false,
      selectedId: null,
      mouseSelectAllowed: true,
      mouseResetTimeout: null,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    hasNextPage() {
      return this.maxTagPages === null || this.tagPage < this.maxTagPages;
    },
  },
  watch: {
    searchTerm() {
      this.searchTags();
    },
  },
  mounted() {
    this.loadTags(true);
  },
  methods: {
    loadTags(reset = false) {
      if (reset) {
        this.tagPage = 0;
        this.maxTagPages = null;
      } else if (this.tagsLoading || !this.hasNextPage) {
        return;
      }

      this.tagPage++;
      this.tagsLoading = true;
      const request = ++this.lastTagsRequest;
      const data = {
        limit: 50,
        page: this.tagPage,
        name: this.searchTerm,
        noCancel: true,
      };
      api('GET', `organisation/${this.currentOrganisation.id}/contact-tags`, data, null, 'w')
        .then((response) => {
          // Ignore the response if a newer request (e.g. search) has been sent in the meantime
          if (request !== this.lastTagsRequest) {
            return;
          }
          this.availableTags = reset ? [...response.data.data] : [...this.availableTags, ...response.data.data];
          this.maxTagPages = response.data.meta.last_page;
          if (reset) {
            this.selectedId = this.searchTerm ? 0 : null;
          }
          if (this.availableTags.length > 5 || this.maxTagPages > 1) {
            this.showSearch = true;
          }
        })
        .catch(() => {
          if (request === this.lastTagsRequest) {
            this.maxTagPages = 0;
          }
        })
        .finally(() => {
          if (request === this.lastTagsRequest) {
            this.tagsLoading = false;
          }
        });
    },
    searchTags: debounce(function f() {
      this.loadTags(true);
    }, 500),
    endOfTagsList(reached) {
      if (reached) {
        this.loadTags();
      }
    },
    isSelected(tag) {
      return this.selectedTags.some(selectedTag => selectedTag.id === tag.id);
    },
    toggleTag(tag) {
      if (!tag) {
        return;
      }
      const tags = this.isSelected(tag) ? this.selectedTags.filter(selectedTag => selectedTag.id !== tag.id) : [...this.selectedTags, tag];
      this.$emit('change', tags);
    },
    clearSelected() {
      this.$emit('change', []);
    },
    clearSearchTerm() {
      this.searchTerm = '';
    },
    focusSearchTermInput() {
      if (this.$refs.searchTermInputRef) {
        this.$refs.searchTermInputRef.focus();
      }
    },
    mouseSelect(index) {
      if (this.mouseSelectAllowed) {
        this.selectedId = index;
      }
    },
    deselect() {
      if (this.mouseSelectAllowed) {
        this.selectedId = null;
      }
    },
    select(index) {
      this.selectedId = index;
      this.$refs.itemListWrapperRef.children[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    },
    /**
     * Sets flag to not react on mouse events on the li items. If this is off and mouse is above a li item and we initiate changes with the arrow keys
     * then at some point the list will scroll and the mouse event will be trigered which will cause unwanted result.
     */
    preventMouseAction() {
      this.mouseSelectAllowed = false;
      clearTimeout(this.mouseResetTimeout);
      this.mouseResetTimeout = setTimeout(() => {
        this.mouseSelectAllowed = true;
      }, 1000);
    },
    /**
     * Handles keys on the search input only. Key events are not propagated so the window listeners of the other lists in the filter popover don't react to them.
     */
    handleKeyDown(event) {
      switch (event.keyCode) {
        case 38: // UP
          this.preventMouseAction();
          if (this.selectedId > 0) {
            this.select(this.selectedId - 1);
          }
          event.preventDefault();
          break;
        case 40: // DOWN
          this.preventMouseAction();
          if (this.selectedId === null && this.availableTags.length) {
            this.select(0);
          } else if (this.selectedId !== null && this.selectedId < this.availableTags.length - 1) {
            this.select(this.selectedId + 1);
          }
          event.preventDefault();
          break;
        case 13: // ENTER
          if (this.selectedId !== null) {
            this.toggleTag(this.availableTags[this.selectedId]);
          }
          event.preventDefault();
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.select-item-box {
  width: 297px;
  font-size: 14px;

  h5 {
    text-align: center;
    font-weight: 500;
    padding: 15px 16px 0;
  }

  .clear-selected {
    line-height: 1;
    position: absolute;
    top: 12px;
    right: 14px;
    color: $blackPrimary;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    max-height: 24px;
    padding: 6px 10px !important;
  }

  .search-input-wrapper {
    position: relative;

    input {
      position: relative;
      padding: 5px 26px 5px 8px;
      margin: 15px 16px 0;
      width: calc(100% - 30px);
      border-radius: 5px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1) !important;
      border: solid 1px #cfd8dc !important;
      background-color: #ffffff !important;
      height: 26px !important;

      &:focus {
        box-shadow: 0 0 0 3px #bfdeff !important;
      }
    }

    .sb-icon {
      position: absolute;
      top: 5px;
      right: 20px;
    }
  }

  .tag-list {
    margin: 0;
    margin-top: 15px;
    padding: 7px 0;
    border-radius: 0 0 5px 5px;
    border-top: solid 1px #e0e7e9;
    max-height: 200px;
    overflow-y: auto;

    li {
      padding: 10px 16px;
      cursor: pointer;

      .item-icon {
        margin-right: 10px;
      }

      .tag-icon {
        width: 20px;
        height: 11px;
        object-fit: contain;
      }

      .done-tick-blue {
        margin-left: 10px;
      }

      &.selected {
        background: $mainBg;
      }

      &.list-info {
        cursor: default;
      }
    }

    .no-match {
      padding: 10px 12px;
    }
  }
}
</style>
